import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Pencil, Search } from "lucide-react";
import { api } from "@/lib/api";
import { CustomerDialog, EMPTY_CUSTOMER as EMPTY } from "@/components/QuickDialogs";
import { CustomerImport } from "@/components/CustomerImport";
import { PrintButton } from "@/components/PrintButton";
import { EstrattiTutti } from "@/components/EstrattiTutti";
import { useAuth, canWrite, isAgent } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";


export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => api.get("/customers").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/customers/${id}`);
    toast.success("Anagrafica eliminata");
    load();
  };

  const list = (items || []).filter((c) => `${c.denominazione} ${c.nome} ${c.cognome} ${c.piva} ${c.codice_fiscale}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Clienti e fornitori" subtitle="Anagrafiche con dati fiscali e recapito SdI">
        <PrintButton testid="print-customers-button" path="/stampe/anagrafiche.pdf" />
        {canWrite(user) && !isAgent(user) && <EstrattiTutti />}
        {canWrite(user) && <div className="flex gap-2">{!isAgent(user) && <CustomerImport onDone={load} />}<Button data-testid="add-customer-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({ ...EMPTY })}><Plus className="mr-2 h-4 w-4" />Nuova anagrafica</Button></div>}
      </PageHeader>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="customer-search-input" className="bg-white pl-9" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {items && list.length === 0 ? <Empty text="Nessuna anagrafica" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="customers-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Denominazione</TableHead><TableHead>Tipo</TableHead><TableHead>P.IVA / C.F.</TableHead><TableHead>Comune</TableHead><TableHead>Recapito SdI</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium"><Link to={`/app/anagrafiche/${c.id}`} data-testid={`open-customer-${c.id}`} className="text-sky-800 hover:underline">{c.codice && <span className="mr-2 font-mono text-xs text-slate-500">{c.codice}</span>}{c.denominazione || `${c.nome} ${c.cognome}`}</Link></TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs capitalize">{c.tipo}</span></TableCell>
                  <TableCell className="font-mono text-xs">{c.piva || c.codice_fiscale}</TableCell>
                  <TableCell className="text-sm">{c.comune} {c.provincia && `(${c.provincia})`}</TableCell>
                  <TableCell className="font-mono text-xs">{c.codice_destinatario !== "0000000" ? c.codice_destinatario : c.pec || "0000000"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {canWrite(user) && !isAgent(user) && <>
                      <Button variant="ghost" size="icon" data-testid={`edit-customer-${c.id}`} onClick={() => setEdit({ ...EMPTY, ...c })}><Pencil className="h-4 w-4" /></Button>
                      <ConfirmDelete testid={`delete-customer-${c.id}`} onConfirm={() => del(c.id)} />
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <CustomerDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
